import { PaymentSettingType, SalesDiscountType, SalesType } from '@/models/sales'

export type SalesPaymentTotalType = {
  id: string
  name: string
  totalDiscount: number
  companyId: string | undefined
}

type SalesPaymentTotalsType = {
  totalDiscount: number
  totalSalesPayments: SalesPaymentTotalType[]
  totalSalesPaymentPrice: number
}

/**
 * 支払い方法ごとの入金額（割引充当後）と入金合計を集計する。
 * 割引は対象支払いと同じ支払い方法の入金に充当するが、対象支払いが「-」（isFixed）の割引は精算に充当されていない。
 * 「-」の精算済み明細に充当すると割引が入金に吸収され、未収金から差し引かれなくなるため充当しない
 * （バックエンドの Reservation::calcUnpaidPrice と同じ扱い）。
 */
export const calcSalesPaymentTotals = (
  usageDetails: SalesType[],
  discounts: SalesDiscountType[],
  salesPayments: PaymentSettingType[],
): SalesPaymentTotalsType => {
  const unselectedPaymentIds = salesPayments.filter(salesPayment => Number(salesPayment.isFixed) === 1).map(salesPayment => salesPayment.id)
  const findTotal = (totals: SalesPaymentTotalType[], id: string, companyId: string | undefined) =>
    // 従来の `==` 比較と同じく、売掛先なしの null と undefined を同一視する
    totals.find(total => total.id === id && (total.companyId ?? undefined) === (companyId ?? undefined))

  const totalSalesPayments = usageDetails.reduce((totals: SalesPaymentTotalType[], usageDetail) => {
    const total = findTotal(totals, usageDetail.salesPaymentId, usageDetail.companyId)
    if (total) {
      total.totalDiscount += Number(usageDetail.salesPaymentPrice)
    } else {
      totals.push({
        id: usageDetail.salesPaymentId,
        name: usageDetail.salesPaymentName,
        totalDiscount: Number(usageDetail.salesPaymentPrice),
        companyId: usageDetail.companyId,
      })
    }
    return totals
  }, [])
  totalSalesPayments.forEach(total => {
    total.totalDiscount = Math.max(total.totalDiscount, 0)
  })

  discounts.forEach(discount => {
    const appliedPrice = unselectedPaymentIds.includes(discount.salesPaymentId) ? 0 : Number(discount.salesSubjectPrice)
    const total = findTotal(totalSalesPayments, discount.salesPaymentId, discount.companyId)
    if (total) {
      total.totalDiscount = Math.max(total.totalDiscount - appliedPrice, 0)
    } else {
      totalSalesPayments.push({
        id: discount.salesPaymentId,
        name: discount.salesPaymentName,
        totalDiscount: Math.max(-appliedPrice, 0),
        companyId: discount.companyId,
      })
    }
  })

  const totalDiscount = discounts.reduce((sum, discount) => sum + Number(discount.salesSubjectPrice), 0)
  const totalSalesPaymentPrice = totalSalesPayments.reduce((sum, total) => sum + total.totalDiscount, 0) + totalDiscount

  return { totalDiscount, totalSalesPayments, totalSalesPaymentPrice }
}
